import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useStoreState } from '~/store';
import Loader from '~/app/common/Loader';
import { appDataLoader } from '~/data/bootstrap/AppDataLoader';
import { connectivityService } from '~/data/connectivity/ConnectivityService';
import { ConnectivitySnapshot } from '~/data/connectivity/types';
import { cancelScheduledDrain, drainOfflineQueueIfReady, scheduleDrainWhenStable } from '~/data/sync/drainOfflineQueue';
import { resetUploadingToWaiting } from '~/data/sync/syncProgressState';

/**
 * Runs after SqliteProvider opens the DB and UserProvider has released the app tree.
 * Order: drain sync queue → bootstrap app/reference + offline cache.
 */
const OfflineSessionOrchestrator: React.FC = ({ children }) => {
  const [bootstrapping, setBootstrapping] = useState<boolean>(!!window.sqlitePlugin);
  const isUserAuthenticated = useStoreState((state) => state.user.isUserAuthenticated);
  const previousOnlineRef = useRef<boolean>(connectivityService.isOnline());

  const runInitialSession = useCallback(async () => {
    if (!isUserAuthenticated) {
      setBootstrapping(false);
      return;
    }

    try {
      resetUploadingToWaiting();
      await drainOfflineQueueIfReady('session-start');
      await appDataLoader.load({ waitForBackground: true });
    } catch (e) {
      console.error('[AppDataLoader] initial session failed:', e);
    } finally {
      setBootstrapping(false);
    }
  }, [isUserAuthenticated]);

  useEffect(() => {
    runInitialSession();
  }, [runInitialSession]);

  useEffect(() => {
    if (!isUserAuthenticated || !window.sqlitePlugin) {
      return;
    }

    let isInitialSnapshot = true;
    previousOnlineRef.current = connectivityService.isOnline();

    const unsubscribe = connectivityService.subscribe((snap: ConnectivitySnapshot) => {
      const isOnline = snap.status === 0;

      if (isInitialSnapshot) {
        isInitialSnapshot = false;
        previousOnlineRef.current = isOnline;
        return;
      }

      const wasOnline = previousOnlineRef.current;
      previousOnlineRef.current = isOnline;

      if (!wasOnline && isOnline) {
        scheduleDrainWhenStable('reconnect');
        void (async () => {
          try {
            await appDataLoader.load({ waitForBackground: false });
          } catch (e) {
            console.error('[AppDataLoader] reconnect refresh failed:', e);
          }
        })();
      }
    });

    return () => {
      unsubscribe();
      cancelScheduledDrain();
    };
  }, [isUserAuthenticated]);

  if (bootstrapping && window.sqlitePlugin) {
    return <Loader size="32" />;
  }

  return <>{children}</>;
};

export default OfflineSessionOrchestrator;
